import { useQueryClient } from '@tanstack/react-query';
import type {
  EgainConversationEndEvent,
  MessageAttachment,
  NiceCxoneConversationEndEvent,
  TalkdeskConversationEndEvent,
  ZendeskConversationEndEvent
} from 'fe-be-utils';
import { useEffect, useRef } from 'react';
import type { GetWidgetChatResponse } from 'server';
import { io, Socket } from 'socket.io-client';
import { widgetKeys } from '../api/keys';
import { WS_URL } from '../constants';

// TODO: Add new event types to this union as needed
type WebSocketEvent =
  | EgainConversationEndEvent['eventType']
  | ZendeskConversationEndEvent['eventType']
  | NiceCxoneConversationEndEvent['eventType']
  | TalkdeskConversationEndEvent['eventType']
  | 'chat:abandoned';

export type WebSocketMessageData =
  | { event: WebSocketEvent; createdAt?: string }
  | { message: string; agentName?: string; createdAt?: string; attachments?: MessageAttachment[] };

export const useChatWebSocket = (orgId: string, agentId: string, chatId: string) => {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket>();

  useEffect(() => {
    if (!orgId || !agentId || !chatId) return;

    // Create Socket.IO connection
    const socket = io(WS_URL, {
      query: { orgId, agentId, chatId },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    socketRef.current = socket;

    // Handle successful connection
    socket.on('connect', () => {
      // Invalidate queries to sync latest data
      queryClient.invalidateQueries({
        queryKey: widgetKeys.chat(chatId)
      });
    });

    // Handle incoming messages
    socket.on('message', (messageData: WebSocketMessageData) => {
      if ('event' in messageData) {
        queryClient.invalidateQueries({ queryKey: widgetKeys.chat(chatId) });
        return;
      }

      const messageText = messageData.message;
      const agentName = messageData.agentName;
      const createdAt = messageData.createdAt;
      const attachments = messageData.attachments;

      // Check if this is a transfer event
      let isTransferEvent = false;
      try {
        if (messageText) {
          const parsed = JSON.parse(messageText);
          isTransferEvent =
            parsed?.eventType === 'zendesk:transfer' ||
            parsed?.eventType === 'eGain:transfer' ||
            parsed?.eventType === 'niceCxone:transfer' ||
            parsed?.eventType === 'talkdesk:transfer';
        }
      } catch {
        // Not a JSON event, treat as regular message
      }

      if (isTransferEvent) {
        // For transfer events, invalidate to refetch the full chat
        queryClient.invalidateQueries({
          queryKey: widgetKeys.chat(chatId)
        });
      } else {
        // For regular messages, optimistically update the cache
        queryClient.setQueryData<GetWidgetChatResponse>(widgetKeys.chat(chatId), (old) => {
          if (!old?.chat) return old;
          const newMessage = {
            role: 'agent' as const,
            content: messageText,
            ...(agentName && { agentName }),
            ...(createdAt && { createdAt }),
            ...(attachments && { attachments })
          };
          return {
            chat: {
              ...old.chat,
              transcript: {
                ...old.chat.transcript,
                messages: [...(old.chat.transcript?.messages || []), newMessage]
              }
            }
          };
        });
      }
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [orgId, agentId, chatId, queryClient]);
};
